import { useEffect, useMemo, useState } from 'react'

import { useLocale, useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import type { MatrixData } from '../../components/MatrixTable'
import type { WizardFarm, WizardProductionUnit } from '../../utils/farmWizard'
import { farmDayKey, farmWallClockToInstant } from '../../utils/farmTime'
import { loadLotStockAndWeightAt } from '../../utils/lotFishMove'
import { loadWaterTableRows } from '../../utils/waterTable'
import { getDefaultMatrixForSpecies, loadCachedMatrix, resolveSfrMatrixPresetKey } from '../../utils/sfrMatrix'
import { loadSfrMatrix } from '../../utils/sfrMatrixApi'
import { resolveEffectiveSfrMatrix } from '../../utils/sfrProfiles'
import { resolveFarmProductionSpeciesKey } from '../../utils/farmProductionSpecies'
import { loadStoredSpecies } from '../../data/productionSpeciesPresets'
import {
  bandIndexForWeight,
  fastingDaysBefore,
  readPlannedTransfer,
  readTransferProtocol,
  reducedRationKg,
  type TransferProtocolBand,
  type TransferRation
} from '../../utils/transferProtocol'

// Findings #210 — the part of "Trasladar lote" that sets the reduced ration
// after the move: the band of the facility's Protocolo de traslado (proposed
// from the lot's last weight, changeable for this move), and the ration for the
// fish that end up in the destination unit at its last temperature − Δ °C.

/** What Lots saves on the lot that arrives; movedOn and the unit come from the move itself. */
export type TransferRationDraft = Omit<TransferRation, 'movedOn' | 'productionUnitId'>

const latestTemperature = (farmId: string, productionUnitId: string): number | null => {
  let best: { date: string; value: number } | null = null
  for (const row of loadWaterTableRows({ farmId, productionUnitId })) {
    if (typeof row.temperature !== 'number' || !Number.isFinite(row.temperature)) continue
    const date = String(row.date ?? '')
    if (!best || date > best.date) best = { date, value: row.temperature }
  }
  return best?.value ?? null
}

const bandLabel = (bands: TransferProtocolBand[], index: number, t: ReturnType<typeof useT>, locale: string): string => {
  const band = bands[index]
  const previous = index > 0 ? bands[index - 1].maxWeightG : null
  const fmt = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 1 })
  if (band.maxWeightG === null) return previous === null ? t('lots.transferProtocol.band.all') : t('lots.transferProtocol.band.above', { min: fmt(previous) })
  return previous === null ? t('lots.transferProtocol.band.upTo', { max: fmt(band.maxWeightG) }) : t('lots.transferProtocol.band.between', { min: fmt(previous), max: fmt(band.maxWeightG) })
}

export default function TransferProtocolFields(props: {
  sourceLot: Lot
  sourceFarm: WizardFarm | undefined
  /** Destination unit (farm + unit) of the fish. */
  destination: { farmId: string; productionUnitId: string } | null
  sourceUnitId: string
  /** Fish in the destination unit after the move, when the form already knows it. */
  fishAfterMove: number | null
  /** `<input type="datetime-local">` value, farm time. */
  moveAt: string
  timeZone: string
  disabled?: boolean
  onChange: (draft: TransferRationDraft | null) => void
}) {
  const t = useT()
  const locale = useLocale()
  const { sourceLot, sourceFarm, destination, onChange } = props
  const bands = useMemo(() => readTransferProtocol(sourceFarm), [sourceFarm])
  const moveAtIso = farmWallClockToInstant(props.moveAt, props.timeZone)
  const planned = readPlannedTransfer(sourceLot)

  const [lotNow, setLotNow] = useState<{ stock: number | null; weightG: number | null } | null>(null)
  useEffect(() => {
    if (!moveAtIso || !props.sourceUnitId) {
      setLotNow(null)
      return
    }
    let cancelled = false
    // The unit's rows are found by farm and unit name too (as in MoveFields).
    const sourceUnitName = ((sourceFarm?.productionUnits ?? []) as WizardProductionUnit[]).find((unit) => unit.id === props.sourceUnitId)?.name
    void loadLotStockAndWeightAt({ farmId: sourceLot.farmId, farmName: sourceFarm?.name, productionUnitId: props.sourceUnitId, productionUnitName: sourceUnitName, lotId: sourceLot.id, asOf: moveAtIso })
      .then((result) => { if (!cancelled) setLotNow(result) })
      .catch(() => { if (!cancelled) setLotNow({ stock: null, weightG: null }) })
    return () => {
      cancelled = true
    }
  }, [sourceLot.farmId, sourceLot.id, props.sourceUnitId, moveAtIso, sourceFarm])

  const speciesKey = resolveFarmProductionSpeciesKey(sourceFarm) ?? 'global'
  const profileSpeciesKey = resolveFarmProductionSpeciesKey(sourceFarm) ?? loadStoredSpecies()
  const [speciesMatrix, setSpeciesMatrix] = useState<MatrixData>(() => loadCachedMatrix('global') ?? getDefaultMatrixForSpecies('global'))
  useEffect(() => {
    let cancelled = false
    void loadSfrMatrix(resolveSfrMatrixPresetKey(speciesKey), { preferRecentLocal: true }).then((result) => {
      if (!cancelled) setSpeciesMatrix(result.matrix)
    })
    return () => {
      cancelled = true
    }
  }, [speciesKey])

  const proposedIndex = bandIndexForWeight(bands, lotNow?.weightG ?? null)
  // '' = proposed band; 'none' = no protocol for this move; otherwise the band index.
  const [choice, setChoice] = useState<string>('')
  const bandIndex = choice === 'none' ? -1 : choice === '' ? proposedIndex : Number(choice)
  const band = bandIndex >= 0 ? bands[bandIndex] ?? null : null

  const temperatureC = destination ? latestTemperature(destination.farmId, destination.productionUnitId) : null
  const fish = props.fishAfterMove ?? lotNow?.stock ?? null
  const matrix = useMemo(() => resolveEffectiveSfrMatrix({
    farm: sourceFarm,
    productionUnit: ((sourceFarm?.productionUnits ?? []) as WizardProductionUnit[]).find((unit) => unit.id === destination?.productionUnitId),
    lot: sourceLot,
    species: profileSpeciesKey,
    speciesDefaultMatrix: speciesMatrix
  }), [sourceFarm, destination?.productionUnitId, sourceLot, profileSpeciesKey, speciesMatrix])
  const ration = band && band.reducedDays > 0
    ? reducedRationKg({ matrix, species: speciesKey, fish, weightG: lotNow?.weightG ?? null, temperatureC, deltaC: band.deltaC })
    : null
  const moveDay = moveAtIso ? farmDayKey(moveAtIso, props.timeZone) : null
  const fastingDays = planned ? fastingDaysBefore(planned.date, planned.band.fastDays) : []

  const draftKey = band ? JSON.stringify([band, ration, temperatureC, fastingDays]) : 'none'
  useEffect(() => {
    onChange(band
      ? { band, fastingDays, temperatureC, reducedKgPerDay: ration?.kgPerDay ?? null, extrapolated: ration?.extrapolated ?? false }
      : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey])

  if (bands.length === 0 || !destination) return null

  const fmt = (value: number, digits = 1) => value.toLocaleString(locale, { maximumFractionDigits: digits })
  const formatDay = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })

  return (
    <div data-testid="lots-move-transfer-protocol" style={{ display: 'grid', gap: 6, padding: 10, borderRadius: 8, border: '1px solid #fcd34d', backgroundColor: '#fffbeb' }}>
      <label style={{ display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4 }}>
        {t('lots.transferProtocol.title')}
        <select
          data-testid="lots-move-transfer-protocol-band"
          value={choice}
          disabled={props.disabled}
          onChange={(event) => setChoice(event.target.value)}
          style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5f5' }}
        >
          <option value="">
            {proposedIndex >= 0
              ? t('lots.transferProtocol.proposed', { band: bandLabel(bands, proposedIndex, t, locale), weight: fmt(lotNow?.weightG ?? 0) })
              : t('lots.transferProtocol.noWeight')}
          </option>
          {bands.map((_, index) => (
            <option key={index} value={String(index)}>{bandLabel(bands, index, t, locale)}</option>
          ))}
          <option value="none">{t('lots.transferProtocol.none')}</option>
        </select>
      </label>
      {planned ? (
        <span style={{ fontSize: 13, color: '#78350f' }} data-testid="lots-move-transfer-protocol-fasting">
          {fastingDays.length > 0
            ? t('lots.transferProtocol.fastingPlanned', { days: fastingDays.map(formatDay).join(', '), date: formatDay(planned.date) })
            : t('lots.transferProtocol.noFasting', { date: formatDay(planned.date) })}
        </span>
      ) : null}
      {band ? (
        <span style={{ fontSize: 13, color: '#78350f' }} data-testid="lots-move-transfer-protocol-ration">
          {band.reducedDays === 0
            ? t('lots.transferProtocol.noReducedDays')
            : ration
              ? t(ration.extrapolated ? 'lots.transferProtocol.rationExtrapolated' : 'lots.transferProtocol.ration', {
                days: band.reducedDays,
                kg: fmt(ration.kgPerDay, 3),
                temperature: fmt(temperatureC ?? 0),
                delta: fmt(band.deltaC),
                calc: fmt((temperatureC ?? 0) - band.deltaC),
                fish: (fish ?? 0).toLocaleString(locale),
                weight: fmt(lotNow?.weightG ?? 0)
              })
              : t('lots.transferProtocol.rationMissing', {
                days: band.reducedDays,
                missing: [
                  temperatureC === null ? t('lots.transferProtocol.missing.temperature') : null,
                  lotNow?.weightG ? null : t('lots.transferProtocol.missing.weight'),
                  fish ? null : t('lots.transferProtocol.missing.stock')
                ].filter(Boolean).join(', ')
              })}
          {moveDay && band.reducedDays > 0 ? ` ${t('lots.transferProtocol.fromDay', { date: formatDay(moveDay) })}` : ''}
        </span>
      ) : null}
    </div>
  )
}
